import { useEffect, useState } from 'react';
import { hardNavigate } from '../utils/navigation';

/**
 * YouTube 認証完了ページ
 *
 * OAuth コールバックからリダイレクトされ、親ウィンドウに postMessage を送って
 * 認証完了 (またはエラー) を通知する。(Canva の CanvaAuthCompletePage と同型)
 */
export function YouTubeAuthCompletePage() {
  const [status, setStatus] = useState<'notifying' | 'closing' | 'redirecting' | 'error'>('notifying');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('youtube_error');

    // 戻り先は成功/失敗いずれでも消費する (同タブ遷移時のみ意味を持つ)。
    // 連携を開始したページを startAuth が控えてある。
    let returnTo = '/profile';
    try {
      returnTo = sessionStorage.getItem('youtube_return_to') || '/profile';
      sessionStorage.removeItem('youtube_return_to');
    } catch {
      /* noop */
    }

    if (error) {
      setStatus('error');
      if (window.opener) {
        // ポップアップ: 元タブ (モーダル) は生存しているので、このタブはエラーを
        // 少し見せてから閉じる。
        setTimeout(() => {
          try { window.close(); } catch { /* noop */ }
        }, 2500);
      } else {
        // 同タブ遷移: 元ページへ戻す。認可キャンセル/失敗でスタジオ文脈を失わせない。
        // 再オープンのループを避けるため pending は破棄する。
        try {
          sessionStorage.removeItem('youtube_pending_scenario');
        } catch {
          /* noop */
        }
        setTimeout(() => hardNavigate(returnTo), 2500);
      }
      return;
    }

    if (window.opener) {
      // ポップアップで開けた場合: 元タブはそのまま (モーダルも生存) なので postMessage
      // で通知し、このタブを閉じる。元タブ側のリスナーが status を再取得する。
      try {
        window.opener.postMessage({ type: 'YOUTUBE_AUTH_SUCCESS' }, window.location.origin);
        setStatus('closing');
        setTimeout(() => {
          window.close();
        }, 1000);
      } catch (e) {
        console.error('Failed to send postMessage:', e);
        setStatus('redirecting');
        setTimeout(() => hardNavigate(returnTo), 1500);
      }
    } else {
      // 同タブ遷移だった場合: 元のページへフルナビゲートで戻す。再マウントで status は
      // 再取得され connected:true になる。ScenarioGenerator 側が pending scenario を見て
      // アップロードモーダルを開き直す。
      setStatus('redirecting');
      setTimeout(() => hardNavigate(returnTo), 1500);
    }
  }, []);

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: '#1e2026',
      color: '#f5f1e6',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{ textAlign: 'center' }}>
        {status !== 'error' && (
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(255, 255, 255, 0.2)',
            borderTopColor: '#ff0000',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px',
          }} />
        )}
        <p style={{ fontSize: '18px', marginBottom: '8px' }}>
          {status === 'error' ? 'YouTube連携に失敗しました' : 'YouTube連携完了'}
        </p>
        <p style={{ fontSize: '12px', color: 'rgba(245, 241, 230, 0.6)' }}>
          {status === 'closing' && 'このタブは自動的に閉じられます...'}
          {status === 'notifying' && '処理を続行しています...'}
          {status === 'redirecting' && 'マイページに戻ります...'}
          {status === 'error' && 'タブを閉じてもう一度お試しください'}
        </p>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}
